import React, { useState, useEffect } from 'react';
import { SectionInstance } from '../types';
import InlineEditText from '../InlineEditText';
import { getLeadForm } from '../../../services/api';
import { ExternalLink, ClipboardList } from 'lucide-react';
import { sfHeadline, sfSub } from './sectionHelpers';

interface LeadFormField {
  id: string;
  type: string;
  label: string;
  placeholder?: string;
  required?: boolean;
  options?: string[];
}

interface LeadFormData {
  form_id: string;
  name: string;
  fields: LeadFormField[];
  submit_button_text?: string;
  success_message?: string;
}

const LeadFormInline: React.FC<{ section: SectionInstance }> = ({ section }) => {
  const c = section.controls;
  const formId = (c.form_id || '').trim();
  const [linkedForm, setLinkedForm] = useState<LeadFormData | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!formId) { setLinkedForm(null); return; }
    let cancelled = false;
    setLoading(true);
    getLeadForm(formId)
      .then((data: LeadFormData) => { if (!cancelled) { setLinkedForm(data); setLoading(false); } })
      .catch(() => { if (!cancelled) { setLinkedForm(null); setLoading(false); } });
    return () => { cancelled = true; };
  }, [formId]);

  // No form selected — show placeholder
  if (!formId) {
    return (
      <section style={{ padding: '80px 24px', background: 'var(--brand-bg-alt, #f8fafc)' }}>
        <div style={{
          maxWidth: 520, margin: '0 auto', textAlign: 'center',
          border: '2px dashed #cbd5e1', borderRadius: 12, padding: '48px 32px',
          color: '#94a3b8',
        }}>
          <ClipboardList size={32} style={{ marginBottom: 12, color: '#94a3b8' }} />
          <p style={{ fontWeight: 600, marginBottom: 8, color: '#64748b' }}>No form selected</p>
          <p style={{ fontSize: '0.875rem', marginBottom: 16 }}>
            Select a lead capture form from the properties panel, or create one first.
          </p>
          <a
            href="/lead-capture"
            target="_blank"
            rel="noreferrer"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: '#2177FF', color: '#fff', fontWeight: 600,
              padding: '8px 18px', borderRadius: 6, fontSize: '0.8rem',
              textDecoration: 'none',
            }}
          >
            Open Lead Capture <ExternalLink size={13} />
          </a>
        </div>
      </section>
    );
  }

  if (loading) {
    return (
      <section style={{ padding: '80px 24px', background: 'var(--brand-bg-alt, #f8fafc)' }}>
        <div style={{ maxWidth: 600, margin: '0 auto', textAlign: 'center', color: '#94a3b8', fontSize: '0.875rem' }}>
          Loading form…
        </div>
      </section>
    );
  }

  if (!linkedForm) {
    return (
      <section style={{ padding: '80px 24px', background: 'var(--brand-bg-alt, #f8fafc)' }}>
        <div style={{
          maxWidth: 520, margin: '0 auto', textAlign: 'center',
          border: '2px dashed #fca5a5', borderRadius: 12, padding: '48px 32px',
          color: '#ef4444',
        }}>
          <p style={{ fontWeight: 600 }}>Form not found</p>
          <p style={{ fontSize: '0.875rem', marginTop: 8 }}>
            The selected form may have been deleted. Please choose another form.
          </p>
        </div>
      </section>
    );
  }

  const submitText = c.submit_text || linkedForm.submit_button_text || 'Submit';

  return (
    <section style={{ padding: '80px 24px', background: 'var(--brand-bg-alt, #f8fafc)' }}>
      <div style={{ maxWidth: 600, margin: '0 auto' }}>
        {/* Linked form badge */}
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          background: 'rgba(30,172,166,0.08)', border: '1px solid rgba(30,172,166,0.25)',
          borderRadius: 6, padding: '4px 10px', marginBottom: 20,
          fontSize: '0.75rem', color: '#1EACA6', fontWeight: 600,
        }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
          </svg>
          {linkedForm.name}
        </div>

        <InlineEditText tag="h2" controlKey="headline" style={{ textAlign: 'center', marginBottom: 8 }}>
          {sfHeadline(c) || linkedForm.name}
        </InlineEditText>
        <InlineEditText
          tag="p"
          controlKey="subheadline"
          style={{ textAlign: 'center', color: 'var(--brand-text-light, #64748b)', marginBottom: 24 }}
        >
          {sfSub(c, '')}
        </InlineEditText>

        <div style={{
          background: 'var(--brand-bg, #fff)', padding: 32,
          borderRadius: 'var(--brand-card-radius, 12px)',
          boxShadow: '0 10px 15px -3px rgba(0,0,0,0.08), 0 4px 6px -4px rgba(0,0,0,0.04)',
        }}>
          {linkedForm.fields.map(f => (
            <div key={f.id} style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', fontWeight: 600, fontSize: '0.875rem', marginBottom: 4 }}>
                {f.label}
                {f.required && <span style={{ color: '#ef4444', marginLeft: 2 }}>*</span>}
              </label>
              {f.type === 'textarea' ? (
                <textarea placeholder={f.placeholder || ''} rows={3} readOnly
                  style={{ width: '100%', padding: 10, border: '1px solid rgba(0,0,0,0.15)', borderRadius: 8, boxSizing: 'border-box', resize: 'none' }} />
              ) : f.type === 'select' ? (
                <select disabled style={{ width: '100%', padding: 10, border: '1px solid rgba(0,0,0,0.15)', borderRadius: 8, boxSizing: 'border-box' }}>
                  <option>Select…</option>
                  {(f.options || []).map((o, i) => <option key={i}>{o}</option>)}
                </select>
              ) : (
                <input type={f.type || 'text'} placeholder={f.placeholder || ''} readOnly
                  style={{ width: '100%', padding: 10, border: '1px solid rgba(0,0,0,0.15)', borderRadius: 8, boxSizing: 'border-box' }} />
              )}
            </div>
          ))}

          <button disabled style={{
            width: '100%', padding: '12px 24px', borderRadius: 6, border: 'none',
            background: 'var(--brand-primary, #1a365d)', color: '#fff',
            fontWeight: 600, cursor: 'default', marginTop: 8,
            boxShadow: '0 4px 14px rgba(0,0,0,0.15)',
          }}>
            {submitText}
          </button>
          {c.privacy_text && (
            <p style={{ fontSize: '0.75rem', color: 'var(--brand-text-light, #94a3b8)', marginTop: 8, textAlign: 'center' }}>
              {c.privacy_text}
            </p>
          )}
        </div>
      </div>
    </section>
  );
};

export default LeadFormInline;
